/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import axios from "axios";
import { APIGateways } from "../../../utility/APIGateways";
import { getAuthHeader } from "../../../utility/auth";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../utility/errorCodes";

const COMPONENT_NAME = "Admin settingsManager/services/UserService";

/**
 * Calls the UpdatePaceUsers lambda in AWS to update all the records from the updateObject
 */
export const updatePaceUsers = async (updateObject) => {
	try {
		let response = await axios.put(`${APIGateways.userAPIEndpoint}`, updateObject, {
			headers: await getAuthHeader(),
		});
		return response.data.result;
	} catch (error) {
		v2.error(`Error updating user role`, {
			additionalData: {
				error: errorToObject(error),
			},
			logCode: errorCodes.UPDATE_USERS_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};

/**
 * Gets all of the User from AWS
 */
export const getAllUsers = async () => {
	try {
		let response = await axios.get(`${APIGateways.userAPIEndpoint}`, {
			headers: await getAuthHeader(),
		});
		return response.data;
	} catch (error) {
		v2.error(`Error getting all users`, {
			additionalData: {
				error: errorToObject(error),
			},
			logCode: errorCodes.GET_USER_LIST_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};

/**
 * This calls the SyncPaceUsers lambda to update the records given.
 */
export const syncUpdatedUsers = async (newUsersListObject) => {
	try {
		let response = await axios.post(`${APIGateways.userAPIEndpoint}`, newUsersListObject, {
			headers: await getAuthHeader(),
		});
		return { ...response.data };
	} catch (error) {
		v2.error(`Error creating new pace users.`, {
			additionalData: {
				error: errorToObject(error),
			},
			logCode: errorCodes.CREATE_NEW_USERS_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		return {
			failedUsers: [...(newUsersListObject.newConnectUsers || []), ...(newUsersListObject.newCognitoUsers || [])],
		};
	}
};

/**
 * This calls the SyncPaceUsers lambda to delete the records given.
 */
export const syncDeletedUsers = async (oldUsersListObject) => {
	try {
		let response = await axios.post(`${APIGateways.userAPIEndpoint}`, oldUsersListObject, {
			headers: await getAuthHeader(),
		});
		return { ...response.data };
	} catch (error) {
		v2.error(`Error creating new pace users.`, {
			additionalData: {
				error: errorToObject(error),
			},
			logCode: errorCodes.CREATE_NEW_USERS_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		return { failedUsers: oldUsersListObject.oldConnectUsers };
	}
};

/**
 * This calls the GetUserById lambda to retrieve the details for a specific user with a given Id.
 */
export const getUserById = async (userId) => {
	try {
		let response = await axios.get(`${APIGateways.userAPIEndpoint}/${userId}`, {
			headers: await getAuthHeader(),
		});
		return response.data;
	} catch (error) {
		v2.error(`Error getting user by ID`, {
			additionalData: {
				error: errorToObject(error),
			},
			logCode: errorCodes.GET_USER_BY_ID_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};

/**
 * This calls the GetUserStatistics lambda to retrieve all of the statistics for PACE users.
 */
export const getUserStatistics = async () => {
	try {
		let response = await axios.get(`${APIGateways.userAPIEndpoint}/statistics`, {
			headers: await getAuthHeader(),
		});
		return response.data.results;
	} catch (error) {
		v2.error(`Error getting user statistics`, {
			additionalData: {
				error: errorToObject(error),
			},
			logCode: errorCodes.GET_USER_BY_ID_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};
/**
 * This calls the UpdateRole lambda to update the role in dynamo DB.
 */
export const updateRole = async (role) => {
	try {
		let response = await axios.post(`${APIGateways.userAPIEndpoint}/roles/update`, role, {
			headers: await getAuthHeader(),
		});
		return response.data.result;
	} catch (error) {
		v2.error(`Error updating role`, {
			additionalData: {
				error: errorToObject(error),
				role,
			},
			logCode: errorCodes.UPDATE_ROLE_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};
/**
 * This calls the deleteRole lambda to delete the role in dynamo DB.
 */
export const deleteRole = async (roleId) => {
	try {
		let response = await axios.delete(`${APIGateways.userAPIEndpoint}/roles/${roleId}`, {
			headers: await getAuthHeader(),
		});
		return response.data;
	} catch (error) {
		v2.error(`Error deleting role`, {
			additionalData: {
				error: errorToObject(error),
			},
			logCode: errorCodes.DELETE_ROLE_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};
/**
 * This calls the getAllRoles lambda to retrieve all roles from dynamo DB.
 */
export const getAllRoles = async () => {
	try {
		let response = await axios.get(`${APIGateways.userAPIEndpoint}/roles`, {
			headers: await getAuthHeader(),
		});
		return response.data.result;
	} catch (error) {
		v2.error(`Error getting all roles`, {
			additionalData: {
				error: errorToObject(error),
			},
			logCode: errorCodes.GET_ALL_ROLES_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};

export const getRolesByRoleId = async (roleIds) => {
	try {
		let response = await axios.post(
			`${APIGateways.userAPIEndpoint}/roles`,
			{ roleIds },
			{
				headers: await getAuthHeader(),
			},
		);
		return response.data.result;
	} catch (error) {
		v2.error(`Error getting user by ID`, {
			additionalData: {
				error: errorToObject(error),
			},
			logCode: errorCodes.GET_USER_BY_ID_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};
